import React from "react";
import styles from './styles.module.scss';
import { Input } from "../input";
import { Selector } from "../selector";
import { Loading } from "../loading";
import { RadioInput } from "../radio";
import { FileUpload } from "../file-upload";
import { sendSupportFormData } from "../../request-service";
import { useTurnstile } from "../../hooks/useTurnstile";

export function ContactForm() {
    const iOS = /iP(ad|hone|od)/.test(navigator.userAgent);

    const defaultFormData = {
        game: '', name: '', email: '', message: '',
        platform: iOS ? 'ios' : 'android', files: []
    };

    const [games, setGames] = React.useState('');
    const [formData, setFormData] = React.useState(defaultFormData);
    const [loading, setLoading] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState('');
    const [errorMessage, setErrorMessage] = React.useState('');
    const { turnstileToken, turnstileRef, isCaptchaConfigured, resetTurnstile } = useTurnstile();

    React.useEffect(() => {
        async function getGames() {
            const response = await fetch("https://admin.applife.io/applications?_sort=Order:DESC");
            const gms = await response.json();
            setGames(gms.map(gm => ({ label: gm.name, value: gm.name })));
        }
        getGames();
    }, []);

    async function handleFormSubmit(event) {
        event.preventDefault();

        if (!turnstileToken) {
            setErrorMessage('Please complete the captcha.');
            return;
        }

        setLoading(true);
        setErrorMessage('');

        const uploadFormData = new FormData();
        const { files, ...restFormData } = formData;

        for (const key in restFormData) {
            if (restFormData.hasOwnProperty(key)) uploadFormData.append(key, restFormData[key]);
        }

        if (files) files.forEach((file) => uploadFormData.append('file', file));

        uploadFormData.append('formType', 'contact');
        uploadFormData.append('turnstileToken', turnstileToken);

        try {
            await sendSupportFormData(uploadFormData);
            setFormData(defaultFormData);
            setLoading(false);
            setSuccessMessage('Your message was successfully sent.');
            resetTurnstile();
        } catch (error) {
            setLoading(false);
            setErrorMessage(error?.message || 'Something went wrong, please try again later');
            resetTurnstile();
        }
    }

    function handleFormChange({ target }) {
        let value = target.value;
        if (target.files) { value = formData.files; Array.prototype.push.apply(value, target.files); }
        setFormData({ ...formData, [target.name]: value });
    }

    function handleFormSelector(data) { setFormData({ ...formData, game: data.value }); }

    function handleFormInputChange({ currentTarget }) {
        if (!currentTarget) return;
        let value = currentTarget.value;
        if (currentTarget.files) { value = []; Array.prototype.push.apply(value, currentTarget.files); }
        setFormData({ ...formData, [currentTarget.name]: value });
    }

    function handleFileUploadFileRemove(file) {
        const copy = [...formData.files];
        const idx = copy.indexOf(file);
        if (idx > -1) { copy.splice(idx, 1); setFormData({ ...formData, files: copy }); }
    }

    function handleMessageClose() { setErrorMessage(''); setSuccessMessage(''); }

    return (
        <div className={styles.contactsForm}>
            <Loading isLoading={loading} errorMessage={errorMessage} successMessage={successMessage} onMessageClose={handleMessageClose} />
            <form onSubmit={handleFormSubmit} onChange={handleFormChange}>
                <Selector label="Game" name="game" id="game" options={games} onChange={handleFormSelector} isRequired={true} />
                <div className={styles.separated}>
                    <div><Input label="Name" value={formData.name} name="name" id="name" /></div>
                    <div><Input label="Email" value={formData.email} name="email" id="email" type="email" /></div>
                </div>
                <Input label="Message" value={formData.message} name="message" id="message" isTextarea />
                <div className={styles.formLineFlex}>
                    <RadioInput name="platform" onChange={handleFormInputChange} value="ios" checked={formData.platform === 'ios'} label="iOS" />
                    <RadioInput name="platform" onChange={handleFormInputChange} value="android" checked={formData.platform === 'android'} label="Android" />
                    <FileUpload name='files' files={formData.files} onChange={handleFormInputChange} onFileRemove={handleFileUploadFileRemove} />
                </div>
                <div className={styles.bottom}>
                    <div style={{ marginBottom: '16px' }}>
                        {isCaptchaConfigured ? <div ref={turnstileRef} /> : <div style={{color:'red'}}>Captcha configuration error.</div>}
                    </div>
                    <div className="formSubmitButtonErrorsWrap contactFormButton">
                        <input type="submit" className="buttonWrap button button-green contactSubmitButton" value="Send Message" />
                    </div>
                </div>
            </form>
        </div>
    );
}